// src/stores/RecordingStore.ts
import { makeAutoObservable, runInAction, action } from "mobx";
import { RecordingManager } from "../models/RecordingManager";
import { IAuthor, IRecording, IRecordingWithAuthor, ITimingData, ContentRating } from "../types";
import { constructUrl } from "../utilities";
import { TagManager } from "../models/TagManager";
import { sortRecordingsForEpisodeMigration } from "../utils/episodeProjectMigration";

class RecordingStoreImpl {
  recordings: Map<string, IRecordingWithAuthor> = new Map();
  recordingsByPinKey: Map<string, IRecordingWithAuthor[]> = new Map();
  /** Episode recordings keyed by project — survives pin list refreshes that call setRecordings. */
  episodeRecordingsByProject: Map<string, IRecordingWithAuthor[]> = new Map();
  lastUpdated: Date = new Date();
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  getRecording(key: string) {
    return this.recordings.get(key);
  }

  async fetchRecordingsForPin(pinKey: string) {
    this.setLoading(true);
    try {
      const recordingsForPin = await RecordingManager.getRecordingsForPin(pinKey);
      runInAction(() => {
        this.recordingsByPinKey.set(pinKey, recordingsForPin);
        recordingsForPin.forEach(recording => {
          this.recordings.set(recording.key, recording);
        });
        this.setLoading(false);
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
        this.setLoading(false);
      });
    }
    return this.recordingsByPinKey.get(pinKey) || [];
  }

  async fetchRecording(key: string) {
    this.setLoading(true);
    try {
      const recording = await RecordingManager.getRecordingWithAuthor(key);
      runInAction(() => {
        if (recording) {
          this.recordings.set(key, recording);
        }
        this.setLoading(false);
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
        this.setLoading(false);
      });
    }
  }

  async updateRecording(key: string, updatedData: Partial<IRecordingWithAuthor>) {
    try {
      await RecordingManager.updateRecording(key, updatedData);
      this.applyRecordingUpdate(key, updatedData);
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
      });
    }
  }

  /** Update in-memory recording state only (author is joined at read time, not persisted). */
  syncRecordingInStore(key: string, updatedData: Partial<IRecordingWithAuthor>) {
    this.applyRecordingUpdate(key, updatedData);
  }

  private applyRecordingUpdate(key: string, updatedData: Partial<IRecordingWithAuthor>) {
    runInAction(() => {
      const existingRecording = this.recordings.get(key);
      if (existingRecording) {
        const updatedRecording = { ...existingRecording, ...updatedData };
        if (!('preview' in updatedData) || updatedData.preview != null) {
          // keep preview as merged
        } else {
          delete updatedRecording.preview;
        }
        this.recordings.set(key, updatedRecording);

        if (updatedRecording.pinKey) {
          const pinRecordings = this.recordingsByPinKey.get(updatedRecording.pinKey) || [];
          this.recordingsByPinKey.set(
            updatedRecording.pinKey,
            pinRecordings.map((recording) => {
              if (recording.key !== key) return recording;
              const next = { ...recording, ...updatedData };
              if ('preview' in updatedData && updatedData.preview == null) {
                delete next.preview;
              }
              return next;
            })
          );
        }
      }
      this.updateEpisodeRecordingInCache(key, updatedData);
      this.lastUpdated = new Date();
    });
  }

  private updateEpisodeRecordingInCache(
    key: string,
    updatedData: Partial<IRecordingWithAuthor>
  ) {
    for (const [projectId, recordings] of this.episodeRecordingsByProject.entries()) {
      const index = recordings.findIndex((recording) => recording.key === key);
      if (index === -1) continue;

      const updatedRecordings = [...recordings];
      const next = { ...updatedRecordings[index], ...updatedData };
      if ('preview' in updatedData && updatedData.preview == null) {
        delete next.preview;
      }
      updatedRecordings[index] = next as IRecordingWithAuthor;
      this.episodeRecordingsByProject.set(
        projectId,
        sortRecordingsForEpisodeMigration(updatedRecordings) as IRecordingWithAuthor[]
      );
    }
  }

  updateRecordingTimingData(key: string, updatedTimingData: ITimingData[]) {
    runInAction(() => {
      const existingRecording = this.recordings.get(key);
      if (existingRecording) {
        existingRecording.timingData = updatedTimingData;
        this.recordings.set(key, existingRecording);
      }
    });
  }

  getRecordingKeysForProject(project_id: string): string[] {
    return Array.from(this.recordings.values())
      .filter(recording => recording.project === project_id)
      .map(recording => recording.key);
  }

  addRecording(recording: IRecordingWithAuthor) {
    runInAction(() => {
      this.recordings.set(recording.key, recording);
      
      // Update recordingsByPinKey
      if (recording.pinKey) {
        const existingRecordings = this.recordingsByPinKey.get(recording.pinKey) || [];
        this.recordingsByPinKey.set(recording.pinKey, [...existingRecordings, recording]);
      }
      
      this.setLastUpdated(new Date());
    });
  }

  setRecordings(newRecordings: IRecordingWithAuthor[]) {
    runInAction(() => {
      this.recordings = new Map();
      this.recordingsByPinKey = new Map();
      newRecordings.forEach((recording) => {
        this.recordings.set(recording.key, recording);
        if (recording.pinKey) {
          const existing = this.recordingsByPinKey.get(recording.pinKey) || [];
          this.recordingsByPinKey.set(recording.pinKey, [...existing, recording]);
        }
      });
      this.mergeEpisodeRecordingsIntoMap();
      this.lastUpdated = new Date();
    });
  }

  private mergeEpisodeRecordingsIntoMap() {
    for (const episodeRecordings of this.episodeRecordingsByProject.values()) {
      episodeRecordings.forEach((recording) => {
        this.recordings.set(recording.key, recording);
      });
    }
  }

  setLoading(loading: boolean) {
    runInAction(() => {
      this.loading = loading;
    });
  }

  setError(error: string | null) {
    runInAction(() => {
      this.error = error;
    });
  }

  removeRecording(key: string) {
    runInAction(() => {
      this.recordings.delete(key);
      for (const [projectId, recordings] of this.episodeRecordingsByProject.entries()) {
        const filtered = recordings.filter((recording) => recording.key !== key);
        if (filtered.length !== recordings.length) {
          this.episodeRecordingsByProject.set(projectId, filtered);
        }
      }
      this.setLastUpdated(new Date());
    });
  }

  updateRecordingAuthor(recordingKey: string, authorData: Partial<IAuthor>) {
    runInAction(() => {
      const recording = this.recordings.get(recordingKey);
      if (recording && recording.author) {
        recording.author = {
          ...recording.author,
          ...authorData,
          tags: {
            authorTags: authorData.tags?.authorTags || recording.author.tags?.authorTags || []
          }
        };
        this.recordings.set(recordingKey, recording);
      }
    });
  }

  async fetchRecordingsForEpisodeProject(project_id: string) {
    this.setLoading(true);
    try {
      const recordings = await RecordingManager.fetchRecordingsForEpisodeProject(project_id);
      const sortedRecordings = sortRecordingsForEpisodeMigration(recordings);
      runInAction(() => {
        this.setEpisodeRecordings(project_id, sortedRecordings);
        this.setLoading(false);
      });
      return this.getEpisodeRecordings(project_id);
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
        this.setLoading(false);
      });
      return [];
    }
  }

  setEpisodeRecordings(project_id: string, recordings: IRecordingWithAuthor[]) {
    runInAction(() => {
      const sortedRecordings = sortRecordingsForEpisodeMigration(recordings) as IRecordingWithAuthor[];
      this.episodeRecordingsByProject.set(project_id, sortedRecordings);
      sortedRecordings.forEach((recording) => {
        this.recordings.set(recording.key, recording);
      });
      this.setLastUpdated(new Date());
    });
  }

  getEpisodeRecordings(project_id: string): IRecordingWithAuthor[] {
    return [...(this.episodeRecordingsByProject.get(project_id) ?? [])];
  }

  async fetchRecordingsForAuthor(authorKey: string) {
    this.setLoading(true);
    try {
      const recordingsForAuthor = await RecordingManager.getRecordingsByAuthorKey(authorKey);
      runInAction(() => {
        this.recordings.clear();
        recordingsForAuthor.forEach(recording => {
          this.recordings.set(recording.key, recording);
        });
        this.setLoading(false);
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
        this.setLoading(false);
      });
    }
  }

  getAudioUrl(recordingKey: string): string | undefined {
    const recording = this.recordings.get(recordingKey);
    if (!recording) return undefined;
    
    // If audioURL is a blob URL, use it directly
    if (recording.file?.audioURL && recording.file.audioURL.startsWith('blob:')) {
      return recording.file.audioURL;
    }
    
    // If audioURL exists and is not a blob URL, use it
    if (recording.file?.audioURL) {
      return recording.file.audioURL;
    }
    
    // Otherwise, construct URL from recordingPath (for Firebase Storage)
    return recording.file?.recordingPath ? constructUrl(recording.file.recordingPath) : undefined;
  }

  setLastUpdated(date: Date) {
    this.lastUpdated = date;
  }

  async updateContentRating(recordingKey: string, contentRating: ContentRating) {
    try {
      await TagManager.updateContentRating(recordingKey, contentRating);
      runInAction(() => {
        const existingRecording = this.recordings.get(recordingKey);
        if (existingRecording) {
          existingRecording.contentRating = contentRating;
          existingRecording.requiresModeration = false;
          this.recordings.set(recordingKey, existingRecording);
        }
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
      });
    }
  }

  async getRecordingsByContentRating(contentRating: ContentRating): Promise<IRecordingWithAuthor[]> {
    try {
      const recordingKeys = await TagManager.getRecordingsByContentRating(contentRating);
      const recordings: IRecordingWithAuthor[] = [];
      
      for (const key of recordingKeys) {
        const recording = this.recordings.get(key);
        if (recording) {
          recordings.push(recording);
        } else {
          // If the recording is not in the store, fetch it
          await this.fetchRecording(key);
          const fetchedRecording = this.recordings.get(key);
          if (fetchedRecording) {
            recordings.push(fetchedRecording);
          }
        }
      }
      
      return recordings;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : String(error);
      });
      return [];
    }
  }
}

const RecordingStore = new RecordingStoreImpl();
RecordingStore.addRecording = action(RecordingStore.addRecording);
RecordingStore.setRecordings = action(RecordingStore.setRecordings);

export default RecordingStore;
